import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

import cx from 'classnames';

import withTheme from 'core/hocs/withTheme';
import { withRouter } from 'core/libs/router';

import themePropTypes from 'core/utils/prop-types/theme';

import { resolveScopedStyles } from 'core/utils/styled-jsx';

import styles from './index.styl';

class Dropdown extends PureComponent {
  static propTypes = {
    /**
     * Функция для получения инстанса дропдауна.
     */
    getInstance: PropTypes.func,

    /**
     * Элемент-триггер
     */
    anchor: PropTypes.node,

    /**
     * Событие срабатывает после открытия дропдауна
     */
    onOpen: PropTypes.func,

    /**
     * Событие срабатывает после закрытия дропдауна
     */
    onClose: PropTypes.func,

    /**
     * Позиция контента дропдауна.
     * left - контент и заголовок выровнены по левому краю
     * right - контент и заголовок выровнены по правому краю
     * center - контент по центру заголовка
     * inside - контент открывается внутри dropdown, под заголовком
     */
    contentPosition: PropTypes.oneOf([
      'left',
      'right',
      'center',
      'inside',
    ]),

    contentPositionY: PropTypes.oneOf([
      'top',
      'bottom',
    ]),

    /**
     * Border raduis контента. Должен быть валидным значением
     * css-свойства `border-radius`.
     */
    contentBorderRadius: PropTypes.string,

    /**
     * transition-duration для свойства opacity контента. Должен быть валидным значением
     * css-свойства `transition-duration`.
     */
    contentOpacityDuration: PropTypes.string,

    /**
     * Внутренний отступ контента. Должен быть валидным css свойством padding
     */
    contentPadding: PropTypes.string,

    /**
     * Задержка перед срабатыванием close
     */
    onCloseDelay: PropTypes.number,

    /**
     * Задержка перед срабатыванием open
     */
    onOpenDelay: PropTypes.number,

    /**
     * Внутренний отступ контента. Должен быть валидным css свойством margin.
     * Позволяет задать дополнительное смещение выпадашки.
     */
    contentMargin: PropTypes.string,

    /**
     * Событие, по которому срабатывает открытие и закрытие дропдауна
     */
    trigger: PropTypes.oneOf([
      'hover',
      'click',
    ]),

    /**
     * Показывать ли иконку
     */
    showIcon: PropTypes.bool,

    /**
     * Желаемый отступ от края экрана при условии наличия свободного места
     */
    sideIndent: PropTypes.number,

    /** CSS-класс, который будет добавлен внешнему div'у дропдауна. */
    className: PropTypes.string,

    /**
     * Закрывать ли дропдаун при смене url
     */
    closeAfterUrlChanged: PropTypes.bool,

    /** @ignore */
    theme: themePropTypes(`{
      icons: {
        ArrowDropdown,
      }
      animations: {
        hover
      },
      controls: {
        dropdown: {
          content: {
            background,
            boxShadowColor,
            border,
          }
        }
      },
    }`),

    /** @ignore */
    location: PropTypes.shape({
      key: PropTypes.string,
    }).isRequired,

    /** @ignore */
    dataQa: PropTypes.string,
  };

  static defaultProps = {
    trigger: 'click',
    contentPosition: 'left',
    contentPositionY: 'bottom',
    contentPadding: '20px',
    contentMargin: '0',
    contentBorderRadius: '0',
    showIcon: true,
    onCloseDelay: 0,
    onOpenDelay: 0,
    sideIndent: 15,
  };

  constructor(props) {
    super(props);

    props.getInstance && props.getInstance(this);
  }

  state = {
    opened: false,
  };

  componentDidMount() {
    if (process.env.BROWSER_RUNTIME) {
      if (this.props.contentPosition === 'inside') {
        const headerHeight = this.anchorEl.clientHeight;
        const padding = window.getComputedStyle(this.contentEl).paddingTop;
        this.contentEl.style.paddingTop = `${10 + headerHeight + parseInt(padding, 10)}px`;
      }
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.closeAfterUrlChanged && this.props.location.key !== prevProps.location.key) {
      this.close();
    }
  }

  componentWillUnmount() {
    this.removeOutsideClickListeners();
  }

  closeTimeOutId;
  openTimeOutId;

  addOutsideClickListeners = () => {
    if (this.props.trigger !== 'click') return;

    if (process.env.BROWSER_RUNTIME) {
      document.addEventListener('mousedown', this.handleOutsideClick);
      document.addEventListener('touchstart', this.handleOutsideClick);
    }

    this.outsideClickListenersAdded = true;
  };

  removeOutsideClickListeners() {
    if (!this.outsideClickListenersAdded) return;

    if (process.env.BROWSER_RUNTIME) {
      document.removeEventListener('mousedown', this.handleOutsideClick);
      document.removeEventListener('touchstart', this.handleOutsideClick);
    }

    this.outsideClickListenersAdded = false;
  }

  handleOutsideClick = event => {
    if (this.dropdownEl && !this.dropdownEl.contains(event.target)) {
      this.close();
    }
  };

  handleStateChanged = () => {
    const { onOpen, onClose } = this.props;

    if (this.state.opened) {
      this.addOutsideClickListeners();
      onOpen && onOpen();
    } else {
      this.removeOutsideClickListeners();
      onClose && onClose();
    }
  };

  /** @public */
  open = () => {
    if (this.closeTimeOutId) {
      clearTimeout(this.closeTimeOutId);
    }
    this.openTimeOutId = setTimeout(() => {
      this.setState({ opened: true }, this.handleStateChanged);
    }, this.props.onOpenDelay);
  };

  /** @public */
  close = () => {
    if (this.openTimeOutId) {
      clearTimeout(this.openTimeOutId);
    }
    this.closeTimeOutId = setTimeout(() => {
      this.setState({ opened: false }, this.handleStateChanged);
    }, this.props.onCloseDelay);
  };

  /** @public */
  toggle = () => {
    this.setState(prevState => ({
      opened: !prevState.opened,
    }), this.handleStateChanged);
  };

  position;

  get positionFix() {
    if (!this.contentEl || !this.state.opened) return 0;

    const {
      left,
      width,
    } = this.contentEl.getBoundingClientRect();

    // определяем отступ от края экрана
    const availableSpace = window.innerWidth - width;
    const indent = availableSpace / 2 < this.props.sideIndent
      ? availableSpace / 2
      : this.props.sideIndent;
    const offsetToWindow = indent >= 0
      ? indent
      : 0;

    // Вылезает слева или шире экрана - сдвигаем к левому краю
    if (left < 0 || width > window.innerWidth) {
      this.position = -left + offsetToWindow;
    // Вылезает справа - сдвигаем влево на нужную величину
    } else if (left + width > window.innerWidth) {
      this.position = -(left + width - window.innerWidth + offsetToWindow);
    }

    return this.position;
  }

  render() {
    const {
      anchor,
      children,
      contentPadding,
      contentMargin,
      contentBorderRadius,
      contentOpacityDuration,
      contentPosition,
      contentPositionY,
      showIcon,
      theme,
      trigger,
      className,
      dataQa,
    } = this.props;

    const { opened } = this.state;

    const {
      icons: {
        ArrowDropdown,
      },
      controls: {
        dropdown: {
          content: contentAtoms,
        },
      },
    } = theme;

    const centeredShift = contentPosition === 'center' ? 'translateX(-50%)' : '';

    const scope = resolveScopedStyles(
      <scope>
        <style jsx>{`
          .dropdown-content
            background ${contentAtoms.background}
            transition opacity ${contentOpacityDuration ? contentOpacityDuration : theme.animations.hover}
            box-shadow 0 10px 20px -5px ${contentAtoms.boxShadowColor}
            border ${contentAtoms.border}
            border-radius ${contentBorderRadius}
            padding ${contentPadding}
            margin ${contentMargin}
        `}</style>
      </scope>
    );

    const hoverHandlers = trigger === 'hover' && {
      onMouseEnter: this.open,
      onMouseLeave: this.close,
    };

    const clickHandlers = trigger === 'click' && {
      onClick: this.toggle,
    };

    return (
      <div
        {...dataQa && { 'data-qa': dataQa }}
        className={cx([
          styles.dropdown,
          opened && styles['dropdown--opened'],
          styles[`dropdown--${contentPosition}`],
          styles[`dropdown--${contentPositionY}`],
          className,
        ])}
        ref={el => {
          this.dropdownEl = el;
        }}
        {...hoverHandlers}
      >
        {/* На dropdown-header ссылается Menu. Не удалять */}
        <div
          className={cx('dropdown-header', styles.dropdownHeader)}
          data-qa='lb-dropdown-header'
          ref={el => {
            this.anchorEl = el;
          }}
          {...clickHandlers}
        >
          {anchor}
          {showIcon && (
            <ArrowDropdown
              width='10'
              height='10'
              className={styles.icon}
            />
          )}
        </div>
        <div
          className={cx(
            scope.wrapClassNames('dropdown-content', styles.dropdownContent),
          )}
          data-qa='lb-dropdown-content'
          ref={el => {
            this.contentEl = el;
          }}
          style={{ transform: `translate(${this.positionFix}px, ${contentPositionY === 'top' ? -100 : 0}%) ${centeredShift}` }}
        >
          {children}
        </div>
        <scope.styles />
      </div>
    );
  }
}

const DropdownWithHOCs = withRouter(withTheme(Dropdown));
DropdownWithHOCs.displayName = 'Dropdown';

export { Dropdown as StorybookComponent };
export default DropdownWithHOCs;
